import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, Settings, Send, Loader2, CheckCircle2, MapPin, Info } from "lucide-react";

const fmtUsd = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtEur = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtCdf = (n) => `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} FC`;

export default function PSQuoteCalculator() {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("quote"); // quote | settings
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    scenario: "fr_to_cd",
    delivery_country: "kinshasa", // kinshasa | france
    full_name: "",
    email: "",
    phone: "",
    country: "",
    purchase_amount_usd: "",
    weight_kg: "",
    kinshasa_zone: "A",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [lastQuote, setLastQuote] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/personal-shopper/settings");
        setSettings(data);
      } catch (e) {
        toast.error(t("psq.load_fail") || "Erreur chargement paramètres");
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live compute mirroring backend logic
  const compute = useMemo(() => {
    if (!settings) return null;
    const amount = Math.max(0, parseFloat(form.purchase_amount_usd) || 0);
    const weight = Math.max(0, parseFloat(form.weight_kg) || 0);
    const tiers = settings.commission_tiers || [];
    const zones = settings.kinshasa_zones || [];
    const fpk = parseFloat(settings.freight_per_kg_usd) || 0;
    const usdCdf = parseFloat(settings.rates?.USD_CDF) || 2900;
    const usdEur = parseFloat(settings.rates?.USD_EUR) || 0.92;

    let pct = tiers[0]?.pct || 5;
    for (const t of tiers) {
      const mn = parseFloat(t.min_usd) || 0;
      const mx = t.max_usd == null ? Infinity : parseFloat(t.max_usd);
      if (amount >= mn && amount <= mx) { pct = parseFloat(t.pct) || 0; break; }
    }
    const commission_usd = amount * pct / 100;
    const freight_usd = weight * fpk;

    const franceFpkRaw = settings.france_freight_per_kg_usd;
    const franceFpk = (franceFpkRaw != null && franceFpkRaw !== "") ? parseFloat(franceFpkRaw) : null;
    let delivery_usd = 0;
    let zoneFee = null;
    if (form.delivery_country === "france") {
      delivery_usd = franceFpk != null ? weight * franceFpk : 0;
    } else {
      const zone = zones.find((z) => z.id === form.kinshasa_zone);
      zoneFee = zone && zone.fee_cdf != null ? parseFloat(zone.fee_cdf) : null;
      delivery_usd = zoneFee != null ? zoneFee / usdCdf : 0;
    }
    const total_usd = amount + commission_usd + freight_usd + delivery_usd;
    return {
      pct,
      commission_usd,
      freight_usd,
      delivery_cdf: zoneFee,
      delivery_usd,
      total_usd,
      total_eur: total_usd * usdEur,
      total_cdf: total_usd * usdCdf,
      fpk,
      franceFpk,
    };
  }, [form, settings]);

  const currentScenario = settings?.scenarios?.find((s) => s.id === form.scenario);
  const scenarioActive = currentScenario?.active !== false;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error(t("psq.err_required") || "Nom et email obligatoires");
      return;
    }
    if (!form.purchase_amount_usd || parseFloat(form.purchase_amount_usd) <= 0) {
      toast.error(t("psq.err_amount") || "Montant d'achat requis");
      return;
    }
    if (!scenarioActive) {
      toast.error(t("psq.err_inactive") || "Ce scénario n'est pas encore disponible");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/personal-shopper/quote", {
        ...form,
        purchase_amount_usd: parseFloat(form.purchase_amount_usd),
        weight_kg: parseFloat(form.weight_kg) || 0,
        kinshasa_zone: form.delivery_country === "kinshasa" ? (form.kinshasa_zone || null) : null,
      });
      setLastQuote(data.quote);
      toast.success(`${t("psq.sent") || "Devis envoyé"} · ${data.quote.quote_number}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error") || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16 text-[#4B5563]">
        <Loader2 className="w-6 h-6 animate-spin mx-auto"/>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <section id="ps-calculator" className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16" data-testid="ps-calculator">
      <div className="text-center max-w-2xl mx-auto mb-8">
        <span className="label-uppercase text-xs text-[#C85A32]">{t("psq.eyebrow") || "CALCULATEUR"}</span>
        <h2 className="heading font-extrabold text-3xl sm:text-4xl text-[#111812] mt-2">
          {t("psq.title") || "Estimez le coût de votre commande"}
        </h2>
        <p className="text-[#4B5563] mt-3">
          {t("psq.subtitle") || "Un devis instantané, sans engagement. Vous validez uniquement si le prix vous convient."}
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="inline-flex bg-[#F5F5F0] p-1 rounded-full mb-6 mx-auto flex" data-testid="psq-tabs">
        <button
          type="button"
          onClick={() => setTab("quote")}
          className={`pill-btn px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "quote" ? "bg-[#1A3322] text-white shadow" : "text-[#4B5563] hover:text-[#1A3322]"}`}
          data-testid="psq-tab-quote"
        >
          <Calculator className="w-4 h-4 inline mr-1.5"/>
          {t("psq.tab_quote") || "Devis"}
        </button>
        <button
          type="button"
          onClick={() => setTab("settings")}
          className={`pill-btn px-5 py-2 rounded-full text-sm font-semibold transition ${tab === "settings" ? "bg-[#1A3322] text-white shadow" : "text-[#4B5563] hover:text-[#1A3322]"}`}
          data-testid="psq-tab-settings"
        >
          <Settings className="w-4 h-4 inline mr-1.5"/>
          {t("psq.tab_settings") || "Paramètres"}
        </button>
      </div>

      {tab === "quote" && (
        lastQuote ? (
          <QuoteSuccess quote={lastQuote} onNew={() => setLastQuote(null)} lang={lang} t={t}/>
        ) : (
          <form onSubmit={submit} className="grid lg:grid-cols-5 gap-6" data-testid="psq-form">
            {/* Left: Inputs */}
            <div className="lg:col-span-3 bg-white border border-[#1A3322]/10 rounded-2xl p-6 shadow-sm space-y-4">
              {/* Scenario */}
              <div className="space-y-1.5">
                <Label>{t("psq.scenario") || "Type de commande"} *</Label>
                <Select value={form.scenario} onValueChange={(v) => set("scenario", v)}>
                  <SelectTrigger data-testid="psq-scenario"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {(settings.scenarios || []).map((s) => (
                      <SelectItem key={s.id} value={s.id} disabled={!s.active} data-testid={`psq-scen-${s.id}`}>
                        {(lang === "en" ? s.label_en : s.label_fr) || s.id} {!s.active && "· " + (t("psq.tbd") || "à venir")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentScenario && (
                  <p className="text-[11px] text-[#4B5563] flex items-start gap-1">
                    <Info className="w-3 h-3 mt-0.5 shrink-0"/>
                    <span>{(lang === "en" ? currentScenario.note_en : currentScenario.note_fr) || ""}</span>
                  </p>
                )}
              </div>

              {/* Client info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("psq.f_name") || "Nom complet"} *</Label>
                  <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="psq-input-name" placeholder="Marie Mbayo"/>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("psq.f_email") || "Email"} *</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="psq-input-email" placeholder="marie@example.com"/>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("psq.f_phone") || "Téléphone"}</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="psq-input-phone" placeholder="+243 812 345 678"/>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("psq.f_country") || "Pays"}</Label>
                  <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="psq-input-country" placeholder="RDC, France…"/>
                </div>
              </div>

              {/* Order params */}
              <div className="border-t border-[#1A3322]/10 pt-4">
                <p className="label-uppercase text-[11px] text-[#C85A32] mb-3 tracking-wider font-bold">{t("psq.order_details") || "Détails de la commande"}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>{t("psq.f_amount") || "Montant achats"} ($) *</Label>
                    <Input type="number" step="0.01" min="0" value={form.purchase_amount_usd} onChange={(e) => set("purchase_amount_usd", e.target.value)} required data-testid="psq-input-amount" placeholder="200.00"/>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("psq.f_weight") || "Poids"} (kg)</Label>
                    <Input type="number" step="0.01" min="0" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="psq-input-weight" placeholder="5.00"/>
                  </div>
                </div>
                {/* Destination */}
                <div className="space-y-1.5 mt-4">
                  <Label>{t("psq.f_destination") || "Destination livraison"} *</Label>
                  <Select value={form.delivery_country} onValueChange={(v) => set("delivery_country", v)}>
                    <SelectTrigger data-testid="psq-destination"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kinshasa" data-testid="psq-dest-kinshasa">🇨🇩 Kinshasa (zones FC)</SelectItem>
                      <SelectItem value="france" data-testid="psq-dest-france">🇫🇷 France ($/kg)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.delivery_country === "kinshasa" && (
                  <div className="space-y-1.5 mt-4">
                    <Label>{t("psq.f_zone") || "Zone de livraison (Kinshasa)"} *</Label>
                    <Select value={form.kinshasa_zone} onValueChange={(v) => set("kinshasa_zone", v)}>
                      <SelectTrigger data-testid="psq-zone"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {(settings.kinshasa_zones || []).map((z) => (
                          <SelectItem key={z.id} value={z.id} disabled={z.fee_cdf == null} data-testid={`psq-zone-${z.id}`}>
                            Zone {z.id} — {(z.communes || []).slice(0, 3).join(", ")}{(z.communes || []).length > 3 ? "…" : ""}
                            {z.fee_cdf != null ? ` · ${fmtCdf(z.fee_cdf)}` : ` · ${t("psq.tbd") || "à définir"}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.delivery_country === "france" && compute?.franceFpk == null && (
                  <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-300 text-xs text-yellow-900 flex items-start gap-2" data-testid="psq-france-warn">
                    <Info className="w-4 h-4 mt-0.5 shrink-0"/>
                    <span>{t("psq.france_tbd") || "Tarif Congo → France pas encore défini. Le devis sera généré avec 0 $ de livraison — notre équipe reviendra vers vous avec le tarif exact sous 24-48h."}</span>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>{t("psq.f_notes") || "Note ou précision"}</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="psq-input-notes" placeholder={t("psq.f_notes_ph") || "Lien produit, marque, taille, référence…"}/>
              </div>
            </div>

            {/* Right: Live summary */}
            <div className="lg:col-span-2">
              <div className="sticky top-4 bg-gradient-to-br from-[#1A3322] to-[#24472F] text-white rounded-2xl p-6 shadow-lg" data-testid="psq-summary">
                <p className="text-[11px] uppercase tracking-widest text-white/70 font-bold">{t("psq.summary") || "Récapitulatif"}</p>
                <p className="heading font-extrabold text-3xl text-[#D98A2C] mt-1">{fmtUsd(compute?.total_usd)}</p>
                <p className="text-xs text-white/60">≈ {fmtEur(compute?.total_eur)} · {fmtCdf(compute?.total_cdf)}</p>

                <div className="mt-5 space-y-2 text-sm">
                  <Row label={t("psq.r_products") || "Achats produit"} value={fmtUsd(parseFloat(form.purchase_amount_usd) || 0)}/>
                  <Row label={`${t("psq.r_commission") || "Commission"} (${compute?.pct?.toFixed(0)}%)`} value={fmtUsd(compute?.commission_usd)}/>
                  <Row label={`${t("psq.r_freight") || "Fret"} (${(compute?.fpk || 0).toFixed(2)}$/kg)`} value={fmtUsd(compute?.freight_usd)}/>
                  <Row
                    label={
                      form.delivery_country === "france"
                        ? `${t("psq.r_delivery") || "Livraison"} 🇫🇷 (${compute?.franceFpk != null ? (compute.franceFpk.toFixed(2) + "$/kg") : (t("psq.tbd") || "à définir")})`
                        : `${t("psq.r_delivery") || "Livraison"} 🇨🇩 Zone ${form.kinshasa_zone}`
                    }
                    value={fmtUsd(compute?.delivery_usd)}
                  />
                </div>
                <div className="border-t border-white/20 mt-4 pt-4 flex justify-between font-extrabold text-lg">
                  <span>{t("psq.r_total") || "Total"}</span>
                  <span className="text-[#D98A2C]">{fmtUsd(compute?.total_usd)}</span>
                </div>

                <Button
                  type="submit"
                  disabled={busy || !scenarioActive}
                  className="w-full mt-5 pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-12 text-base font-bold"
                  data-testid="psq-submit"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>{t("common.loading") || "Envoi..."}</> : <><Send className="w-4 h-4 mr-2"/>{t("psq.submit") || "Recevoir mon devis"}</>}
                </Button>
                {!scenarioActive && (
                  <p className="text-[11px] text-center mt-3 text-white/70">{currentScenario?.[lang === "en" ? "note_en" : "note_fr"]}</p>
                )}
                <p className="text-[10px] text-center mt-3 text-white/50">
                  {t("psq.disclaimer") || "Estimation indicative — le prix final est confirmé sous 24-48h."}
                </p>
              </div>
            </div>
          </form>
        )
      )}

      {tab === "settings" && <SettingsView settings={settings} lang={lang} t={t}/>}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-white/85">
      <span>{label}</span><span className="font-semibold">{value}</span>
    </div>
  );
}

function QuoteSuccess({ quote, onNew, lang, t }) {
  const pdfUrl = `/personal-shopper/devis/${quote.id}`;
  return (
    <div className="max-w-2xl mx-auto bg-white border border-green-200 rounded-2xl p-8 text-center shadow-sm" data-testid="psq-success">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-9 h-9 text-green-600"/>
      </div>
      <p className="text-[11px] uppercase tracking-widest text-[#C85A32] font-bold">{t("psq.your_quote") || "Votre devis"}</p>
      <p className="heading font-extrabold text-3xl text-[#1A3322] font-mono mt-1">{quote.quote_number}</p>
      <p className="heading font-extrabold text-4xl text-[#C85A32] mt-3">${quote.total_usd.toFixed(2)}</p>
      <p className="text-xs text-[#4B5563]">≈ €{quote.total_eur.toFixed(2)} · {Math.round(quote.total_cdf).toLocaleString("fr-FR")} FC</p>
      <p className="text-[#4B5563] mt-5">
        {t("psq.success_desc") || "Nous vous avons envoyé le devis par email. Notre équipe vous contactera sous 24-48h pour confirmer et lancer votre commande."}
      </p>
      <div className="flex gap-3 justify-center mt-6 flex-wrap">
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="psq-pdf-link">
          <Button className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-11 px-6 font-bold text-white">
            📄 {t("psq.download_pdf") || "Télécharger le devis (PDF)"}
          </Button>
        </a>
        <Button onClick={onNew} variant="outline" className="rounded-full h-11 px-6" data-testid="psq-new">
          {t("psq.new_quote") || "Nouveau devis"}
        </Button>
      </div>
    </div>
  );
}

function SettingsView({ settings, lang, t }) {
  const rates = settings.rates || {};
  const franceFpk = settings.france_freight_per_kg_usd;
  const franceMissing = franceFpk == null || franceFpk === "";
  return (
    <div className="space-y-6" data-testid="psq-settings-view">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ParamCard label={t("psq.p_usd_eur") || "USD → EUR"} value={rates.USD_EUR?.toFixed(4)}/>
        <ParamCard label={t("psq.p_usd_cdf") || "USD → CDF"} value={rates.USD_CDF?.toLocaleString("fr-FR")}/>
        <ParamCard label={t("psq.p_freight") || "Fret / kg"} value={`$${Number(settings.freight_per_kg_usd || 0).toFixed(2)}`}/>
        <div
          className={`rounded-xl p-4 text-center border ${franceMissing ? "bg-yellow-50 border-yellow-300" : "bg-white border-[#1A3322]/10"}`}
          data-testid="psq-france-freight-card"
        >
          <p className="label-uppercase text-[10px] tracking-wider font-bold text-[#4B5563]">
            {t("psq.p_france_freight") || "Livraison Congo → France / kg"}
          </p>
          <p className={`heading font-extrabold text-2xl mt-1 ${franceMissing ? "text-yellow-800" : "text-[#1A3322]"}`}>
            {franceMissing ? (t("psq.tbd") || "à définir") : `$${Number(franceFpk).toFixed(2)}`}
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#1A3322]/10 rounded-2xl p-6 shadow-sm">
        <p className="label-uppercase text-xs text-[#C85A32] font-bold">{t("psq.p_tiers") || "Commissions par palier"}</p>
        <h3 className="heading font-extrabold text-xl text-[#111812] mt-1 mb-4">
          {t("psq.p_tiers_title") || "Commission SUKANACOOP sur vos achats"}
        </h3>
        <table className="w-full text-sm" data-testid="psq-tiers-table">
          <thead>
            <tr className="text-left border-b border-[#1A3322]/10">
              <th className="py-2 font-semibold text-[#4B5563]">{t("psq.p_col_range") || "Montant achats ($)"}</th>
              <th className="py-2 text-right font-semibold text-[#4B5563]">{t("psq.p_col_pct") || "Commission SUKANACOOP"}</th>
            </tr>
          </thead>
          <tbody>
            {(settings.commission_tiers || []).map((tier, i) => (
              <tr key={i} className="border-b border-[#1A3322]/5">
                <td className="py-2.5">
                  ${Number(tier.min_usd).toFixed(0)} — {tier.max_usd == null ? `${t("psq.and_more") || "et plus"}` : `$${Number(tier.max_usd).toFixed(0)}`}
                </td>
                <td className="py-2.5 text-right font-bold text-[#C85A32]">{Number(tier.pct).toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#1A3322]/10 rounded-2xl p-6 shadow-sm">
        <p className="label-uppercase text-xs text-[#C85A32] font-bold">{t("psq.p_zones") || "Livraison Kinshasa"}</p>
        <h3 className="heading font-extrabold text-xl text-[#111812] mt-1 mb-4">
          <MapPin className="w-5 h-5 inline mr-1.5 text-[#1A3322]"/>
          {t("psq.p_zones_title") || "Tarifs de livraison par zone (FC)"}
        </h3>
        <div className="space-y-2" data-testid="psq-zones-list">
          {(settings.kinshasa_zones || []).map((z) => (
            <div key={z.id} className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-4 p-3 bg-[#F5F5F0] rounded-lg">
              <div className="flex-1">
                <p className="font-bold text-[#1A3322]">Zone {z.id}</p>
                <p className="text-xs text-[#4B5563] leading-relaxed">{(z.communes || []).join(", ")}</p>
                {!z.fee_cdf && (z.note_fr || z.note_en) && (
                  <p className="text-[11px] text-[#C85A32] mt-1 italic">
                    {lang === "en" ? z.note_en : z.note_fr}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                {z.fee_cdf != null ? (
                  <p className="font-extrabold text-lg text-[#C85A32]">{z.fee_cdf.toLocaleString("fr-FR")} FC</p>
                ) : (
                  <p className="font-semibold text-[#4B5563] text-sm">{t("psq.tbd") || "à définir"}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#1A3322]/10 rounded-2xl p-6 shadow-sm">
        <p className="label-uppercase text-xs text-[#C85A32] font-bold">{t("psq.p_scenarios") || "Scénarios de commande"}</p>
        <h3 className="heading font-extrabold text-xl text-[#111812] mt-1 mb-4">
          {t("psq.p_scenarios_title") || "Où nous intervenons"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="psq-scenarios-list">
          {(settings.scenarios || []).map((s) => (
            <div key={s.id} className={`p-3 rounded-lg border ${s.active ? "border-green-300 bg-green-50" : "border-[#1A3322]/10 bg-[#F5F5F0] opacity-70"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s.active ? "bg-green-500" : "bg-gray-400"}`}/>
                <p className="font-bold text-[#1A3322]">{lang === "en" ? s.label_en : s.label_fr}</p>
                {!s.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D98A2C]/20 text-[#C85A32] font-bold uppercase tracking-wider">{t("psq.tbd") || "à venir"}</span>}
              </div>
              <p className="text-xs text-[#4B5563] mt-1.5">{lang === "en" ? s.note_en : s.note_fr}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ParamCard({ label, value }) {
  return (
    <div className="bg-white border border-[#1A3322]/10 rounded-xl p-4 text-center">
      <p className="label-uppercase text-[10px] text-[#4B5563] tracking-wider font-bold">{label}</p>
      <p className="heading font-extrabold text-2xl text-[#1A3322] mt-1">{value}</p>
    </div>
  );
}
